import { Field, FieldArray, FieldArrayRenderProps } from 'formik';
import React from 'react';
import {
  Grid,
  GridItem,
  Select,
  TextInput,
  NavigationItem,
  BodyText,
  SmallBodyText,
  BoldHeader4Text,
  ISelectOption,
} from 'ui-toolkit';
import { v4 } from 'uuid';
import { showError } from '../../../../../utilities/order/showError';
import { EditSignatoryDataEntry } from './EditSignatoryDataEntry';
import { IPartySchema, ISignatorySchema, useFocusTrackingState } from '../../../../../state';
import styled from 'styled-components';
import { PartyTypeSelector } from './PartyTypeSelector';
import { optionsFromDisplay } from '../../../../../ui-toolkit/enums/optionsFromDisplay';
import { DisplayEntityType } from '../../../../../ui-toolkit/enums/eEntityType';
import { EditAddressDataEntry } from '../../../../../ui-toolkit/orders/components/EditPropertyAddress/EditAddressDataEntry';
import { IFieldChildProps } from '../../../../../ui-toolkit/utilities/formikUtilities';

interface IEditEntityDataEntryProps {
  isNew: boolean;
}

const SigGrid = styled(Grid)`
  grid-column: span 12;
`;
const SpanField = styled.div`
  grid-column: span 6;
`;
const Header = styled(BoldHeader4Text)`
  white-space: nowrap;
`;

export const EditEntityDataEntry: React.FC<IEditEntityDataEntryProps> = ({ isNew }) => {
  const [focus, setFocus] = useFocusTrackingState();

  const optionsEntityType = optionsFromDisplay(DisplayEntityType);
  const filterOptions = (option: ISelectOption<string>, value: string) => {
    return option.value.toLowerCase().includes((value ?? '').toLowerCase());
  };
  const onChangeToState = (value: ISelectOption<string> | null) => value?.value ?? null;
  const transformValueToSelect = (value: string) => optionsEntityType.find((t) => t.value === value);
  const onPartyTypeChange = () => setFocus({ doesAnyone: true, index: -1 });

  return (
    <Grid columnCount={12}>
      {isNew ? <PartyTypeSelector onPartyTypeChange={onPartyTypeChange} /> : null}
      <Field
        name={`entity_name`}
        render={({ field, form }: IFieldChildProps<string, IPartySchema>) => {
          return (
            <SpanField>
              <TextInput
                {...field}
                data-test-id={field.name}
                errorText={showError(form, field.name)}
                label={`Entity Name`}
                onChange={(value) => form.setFieldValue(field.name, value)}
                isRequired={true}
                placeholder={'ABC Real Estate Company'}
                autoFocus={focus.doesAnyone && focus.index === -1}
              />
            </SpanField>
          );
        }}
      />
      <Field
        name={`entity_type`}
        render={({ field, form }: IFieldChildProps<string, IPartySchema>) => (
          <SpanField>
            <Select
              {...field}
              data-test-id={field.name}
              value={transformValueToSelect(field.value)}
              options={optionsEntityType}
              filterOption={filterOptions}
              onChange={(value: any) => form.setFieldValue(field.name, onChangeToState(value))}
              errorText={showError(form, field.name)}
              label={`Entity Type`}
              isRequired={true}
              placeholder={`Select one...`}
            />
          </SpanField>
        )}
      />
      <GridItem columnStart={'span 12'}>
        <Header>Current Address</Header>
      </GridItem>
      <GridItem columnStart={'span 12'}>
        <EditAddressDataEntry name={'address'} />
      </GridItem>
      <Field
        name={`signatories`}
        render={({ field }: IFieldChildProps<ISignatorySchema[], IPartySchema>) => (
          <FieldArray
            name={`signatories`}
            render={({ push, remove }: FieldArrayRenderProps) => (
              <>
                <SigGrid columnCount={2}>
                  {field.value.map((item: ISignatorySchema, indexOfSignatory: number) => (
                    <GridItem key={item.id}>
                      <EditSignatoryDataEntry
                        indexOfSignatory={indexOfSignatory}
                        onClickDelete={() => remove(indexOfSignatory)}
                        autoFocus={focus.doesAnyone && focus.index === indexOfSignatory}
                      />
                    </GridItem>
                  ))}
                </SigGrid>
                <SpanField>
                  <BodyText>
                    <NavigationItem
                      onClick={() => {
                        push({ id: v4(), isNew: true });
                        setFocus({ doesAnyone: true, index: field.value.length });
                      }}
                      text={'+ Add Signatory'}
                      isInline={true}
                    >
                      <SmallBodyText>+ Add Signatory</SmallBodyText>
                    </NavigationItem>
                  </BodyText>
                </SpanField>
              </>
            )}
          />
        )}
      />
    </Grid>
  );
};
